import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { useCart } from "../context/CartContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  ShoppingBag, 
  Users,
  Sparkles,
  ArrowLeft,
  Minus,
  Plus,
  Download,
  FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import TrackPreviewPlayer from "../components/TrackPreviewPlayer";
import { NoRefundsBanner } from "../components/CheckoutBanners";
import { PurchaseReview, QuoteSummary, usePurchaseQuote } from "../components/PurchaseReview";
import { SUBCATEGORY_LABELS } from "../lib/productSubcategories";

const ProductPage = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, token, user } = useAuth();
  const { addItem, setDrawerOpen } = useCart();
  const [product, setProduct] = useState(null);
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [quantity, setQuantity] = useState(1);
  // Tax confirmation state — set by <TaxAddressConfirm /> below and consumed
  // by the buy CTA to gate purchase.
  const [reviewPurchase, setReviewPurchase] = useState(null);
  const quoteState = usePurchaseQuote([{ item_type: "product", item_id: productId, quantity }], !!product);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const productRes = await axios.get(`${API}/products/${productId}`);
      setProduct(productRes.data);
      
      // Fetch artist
      const artistRes = await axios.get(`${API}/artists/${productRes.data.artist_id}`);
      setArtist(artistRes.data);
    } catch (e) {
      console.error("Failed to fetch product:", e);
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async () => {
    if (!isAuthenticated) {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + `/products/${productId}`;
      window.location.href = "/auth?next=" + encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }

    setReviewPurchase({ item_type: "product", item_id: productId, quantity });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#050810] flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag className="w-16 h-16 text-[#8A92A0] mx-auto mb-4" />
          <h1 className="font-['Unbounded'] text-2xl text-white mb-2">Product Not Found</h1>
          <Link to="/discover" className="text-[#EC008C]">Browse Products</Link>
        </div>
      </div>
    );
  }

  const totalPrice = (product.price * quantity).toFixed(2);

  return (
    <div className="min-h-screen bg-[#050810]" data-testid="product-page">
      <PurchaseReview purchase={reviewPurchase} onClose={() => setReviewPurchase(null)} />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-strong">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="logo-link">
            <img src="/logo.png" alt="StardomFans" className="w-10 h-10 object-contain" style={{ mixBlendMode: "lighten" }} />
          </Link>
          <Button 
            variant="ghost" 
            onClick={() => navigate(-1)}
            className="text-[#8A92A0] hover:text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      {/* Product Content */}
      <main className="pt-24 pb-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="space-y-4">
              <div className="aspect-square rounded-2xl bg-[#0A0E18] border border-[#1A2030] flex items-center justify-center overflow-hidden">
                {product.images && product.images.length > 0 ? (
                  <img src={product.images[0]} alt={product.name} className="w-full h-full object-contain rounded-2xl bg-black" />
                ) : (
                  <img src="https://customer-assets.emergentagent.com/job_creator-vault-54/artifacts/am5ifx80_Korean%20idols%20in%20casual%20fashion.%20%282%29.png" alt={product.name} className="w-full h-full object-cover rounded-2xl" />
                )}
              </div>

              {/* Professional messaging beneath the artwork — voting & no-account CTA */}
              {(product.category === "digital" || product.category === "album" || product.preview_url) && (
                <div className="rounded-2xl border border-[#EC008C]/25 bg-[#EC008C]/[0.04] p-5 space-y-3" data-testid="product-vote-cta">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-[#EC008C]/15 border border-[#EC008C]/25 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-[#EC008C]" />
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold mb-1">Every purchase = one vote</p>
                      <p className="text-sm text-white leading-snug">
                        Buying this track counts as your vote for{" "}
                        {artist ? <Link to={`/artist/${artist.artist_id}`} className="text-[#EC008C] font-semibold">{artist.stage_name}</Link> : "this artist"} in the{" "}
                        <Link to="/awards/2027" className="text-[#EC008C] underline">2027 StardomFans Top Global Music Artist</Link> awards.
                      </p>
                      <p className="text-xs text-[#8A92A0] mt-2 leading-relaxed">
                        Same buyer, same product = one vote &amp; free unlimited redownloads.
                        Different tracks from the same artist? Every one is another vote.
                        <strong className="text-white"> Authentic counts, always.</strong>
                      </p>
                    </div>
                  </div>
                  {!user && (
                    <div className="pt-3 border-t border-[#EC008C]/15">
                      <p className="text-xs text-[#8A92A0]">
                        No account?{" "}
                        <Link to="/auth" className="text-[#00E68A] font-semibold hover:underline" data-testid="no-account-cta">
                          Create your account in 60 seconds
                        </Link>{" "}
                        to buy, download and cast votes.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="space-y-6">
              <div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-[#D14A85]/10 text-[#D14A85] mb-4">
                  {product.category}
                </span>
                <h1 className="font-['Unbounded'] text-3xl md:text-4xl font-bold text-white mb-4" data-testid="product-name">
                  {product.name}
                </h1>
                
                {artist && (
                  <Link 
                    to={`/artist/${artist.artist_id}`}
                    className="inline-flex items-center gap-3 mb-6 group"
                    data-testid="product-artist-link"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#1A2030] overflow-hidden">
                      {artist.profile_image ? (
                        <img src={artist.profile_image} alt={artist.stage_name} className="w-full h-full object-cover" />
                      ) : (
                        <Users className="w-full h-full p-2 text-[#8A92A0]" />
                      )}
                    </div>
                    <span className="text-[#8A92A0] group-hover:text-[#EC008C] transition-colors">
                      by {artist.stage_name}
                    </span>
                  </Link>
                )}

                <p className="font-['Unbounded'] text-4xl font-bold text-[#EC008C] mb-4" data-testid="product-price">
                  {product.price} Tokens
                </p>

                {product.description && (
                  <p className="text-[#8A92A0] leading-relaxed">{product.description}</p>
                )}
              </div>

              {/* 30-second preview player — for digital/album products only. Public, no auth needed. */}
              {product.preview_url && (
                <TrackPreviewPlayer
                  previewUrl={product.preview_url}
                  trackName={product.name}
                  price={product.price}
                  currencySymbol={""}
                  priceSuffix={" Tokens"}
                  productId={product.product_id}
                  onBuyClick={handlePurchase}
                />
              )}

              {/* Digital download badge — instant delivery after purchase */}
              {(product.category === "digital" || product.category === "album" || product.digital_file_url) && (
                <div className="card p-4 border-[#EC008C]/40 bg-[#EC008C]/5 flex items-center gap-3" data-testid="product-digital-badge">
                  <div className="w-10 h-10 rounded-lg bg-[#EC008C]/15 flex items-center justify-center flex-shrink-0">
                    <Download className="w-5 h-5 text-[#EC008C]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm">Instant download after purchase</p>
                    <p className="text-[#8A92A0] text-xs mt-0.5">
                      {product.preview_url
                        ? "Taste-test the 30s preview above. When you buy, the full track (MP3 / WAV / ZIP) lands in your Purchases inbox — download unlimited times."
                        : "You'll get the file (MP3 / WAV / ZIP) in your Purchases inbox — download unlimited times."}
                    </p>
                  </div>
                </div>
              )}

              {/* Detailed product specs — attributes (size, colour, material, etc.) — shows for
                  any product with structured attributes, subcategory optional */}
              {product.attributes && Object.entries(product.attributes)
                .filter(([, v]) => v !== null && v !== "" && (!Array.isArray(v) || v.length > 0))
                .length > 0 && (
                <div className="card p-6" data-testid="product-detail-fields">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="w-4 h-4 text-[#EC008C]" />
                    <h3 className="font-['Unbounded'] text-base font-semibold text-white">
                      {product.subcategory ? `${SUBCATEGORY_LABELS[product.subcategory] || "Product"} details` : "Product details"}
                    </h3>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {Object.entries(product.attributes)
                      .filter(([, v]) => v !== null && v !== "" && (!Array.isArray(v) || v.length > 0))
                      .map(([key, value]) => (
                        <div key={key} className="flex flex-col text-sm border-b border-[#1A2030] pb-2" data-testid={`product-attr-${key}`}>
                          <dt className="text-xs text-[#8A92A0] uppercase tracking-wider mb-0.5">
                            {key.replace(/_/g, " ")}
                          </dt>
                          <dd className="text-white break-words">
                            {Array.isArray(value) ? value.join(", ") : (typeof value === "boolean" ? (value ? "Yes" : "No") : String(value))}
                          </dd>
                        </div>
                      ))}
                  </dl>
                </div>
              )}

              <div className="card p-6 space-y-6">
                {/* Token-only economy — currency selector removed. Payouts convert to bank currency at wire time. */}
                <div className="rounded-lg border border-[#EC008C]/30 bg-[#EC008C]/[0.04] px-3 py-2 text-xs text-[#EC008C] font-semibold uppercase tracking-widest" data-testid="token-economy-label">
                  Priced in Tokens · 1 Token = $1 USD (wallet debit)
                </div>

                <div>
                  <p className="text-[#8A92A0] text-sm mb-3">Quantity</p>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                      disabled={quantity <= 1}
                      data-testid="quantity-minus-btn"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                    <span className="text-white text-xl font-semibold w-12 text-center" data-testid="quantity-display">
                      {quantity}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="border-[#1A2030] text-white hover:bg-[#0A0E18]"
                      disabled={quantity >= product.stock}
                      data-testid="quantity-plus-btn"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-[#8A92A0] mt-2">{product.stock} in stock</p>
                </div>

                <div className="border-t border-[#1A2030] pt-4 space-y-3">
                  <NoRefundsBanner isDigital={!product.requires_shipping} />
                  <QuoteSummary state={quoteState} prefix="product" />

                  <div className="flex items-center justify-between">
                    <span className="text-[#8A92A0]">Total</span>
                    <span className="text-white font-semibold text-xl" data-testid="product-total">{totalPrice} Tokens</span>
                  </div>

                  <Button
                    onClick={handlePurchase}
                    disabled={purchasing || product.stock === 0 || quoteState.loading || !quoteState.quote}
                    className="w-full bg-[#EC008C] text-[#000000] hover:bg-[#FF3399] font-semibold h-12 neon-primary"
                    data-testid="purchase-btn"
                  >
                    {product.stock === 0 ? "Out of Stock" : "Review purchase"}
                  </Button>

                  <Button
                    onClick={() => {
                      const ok = addItem(
                        {
                          product_id: product.product_id,
                          name: product.name,
                          price: product.price,
                          image_url: product.image_url,
                          category: product.category,
                          requires_shipping: product.requires_shipping,
                          artist_id: product.artist_id,
                          artist_name: artist?.stage_name || null,
                        },
                        quantity
                      );
                      if (ok) setDrawerOpen(true);
                    }}
                    disabled={product.stock === 0}
                    variant="outline"
                    className="w-full mt-3 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C]/10 h-12 font-semibold"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Add to Cart
                  </Button>
                </div>

                <p className="text-xs text-[#8A92A0] text-center">
                  Secure checkout
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-[#1A2030]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <img src="/logo.png" alt="SF" className="w-10 h-10 object-contain" style={{mixBlendMode:"lighten"}} />
          <p className="text-[#8A92A0] text-sm">&copy; {new Date().getFullYear()} StardomFans Ltd.</p>
        </div>
      </footer>
    </div>
  );
};

export default ProductPage;
